import { useEffect, useRef } from 'react';

// Libs
import QRCode from 'easyqrcodejs';

// RDS
import Button from '@ramsey-design-system/button';
import {
  DownloadIcon,
  DownloadOutlinedIcon
} from '@ramsey-design-system/icons';

// Utils
import { analyticsLogger } from '~fpu/shared/utils/analytics';

interface QrCodeProps {
  url: string;
  qrClassName?: string;
  size?: number;
  logo?: string;
  hasIcon?: boolean;
  iconType?: 'download' | 'downloadOutlined';
  iconPosition?: 'left' | 'right';
  elementId?: string;
  download?: boolean;
  fileName?: string;
  buttonText?: string;
  className?: string;
  buttonStyle?:
    | 'primary'
    | 'alternate'
    | 'ghost'
    | 'subtle'
    | 'neutralSubtle'
    | 'danger'
    | 'dangerSubtle';
  buttonIsOnDarkCanvas?: boolean;
  showCancelButton?: boolean;
  onCancel?: () => void;
}

function QrCode({
  url,
  size,
  qrClassName,
  logo,
  hasIcon = true,
  iconType,
  iconPosition,
  elementId,
  download,
  fileName,
  buttonText = 'Download',
  className,
  buttonStyle,
  buttonIsOnDarkCanvas,
  showCancelButton,
  onCancel,
  ...props
}: QrCodeProps & Record<string, unknown>) {
  const code = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let qrcode: QRCode | null = null;
    if (code.current) {
      while (code.current.firstChild) {
        code.current.removeChild(code.current.firstChild);
      }

      const opts = {
        text: url,
        width: size || 256,
        height: size || 256,
        logo: logo ? logo : null
      };
      const options = { ...props, ...opts };
      qrcode = new QRCode(code.current, options);
    }

    return () => {
      if (qrcode) {
        qrcode.makeCode('');
      }
    };
  }, [props, url, logo, size]);

  const downloadQRCode = () => {
    if (code.current) {
      const canvas = code.current.querySelector('canvas');
      if (canvas) {
        const link = document.createElement('a');
        link.download = fileName || 'qr-code.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    }
    analyticsLogger.trackElementClicked({
      element: elementId || 'QR Code',
      label: 'QR download'
    });
  };

  let iconToRender;
  if (hasIcon) {
    iconToRender =
      iconType === 'downloadOutlined' || !iconType
        ? DownloadOutlinedIcon
        : DownloadIcon;
  }

  return (
    <div className={`${className} gap-md flex flex-col items-center`}>
      <div
        data-testid="cohort-qr-code"
        className={qrClassName}
        ref={code}
      ></div>
      <div className="flex flex-row">
        {download && (
          <Button
            icon={iconToRender}
            appearance={buttonStyle || 'subtle'}
            isOnDarkCanvas={buttonIsOnDarkCanvas}
            iconPosition={iconPosition}
            className="mt-1 flex px-1 font-semibold !no-underline"
            onClick={downloadQRCode}
          >
            {buttonText}
          </Button>
        )}
        {showCancelButton && (
          <Button
            appearance="subtle"
            onClick={onCancel}
            className="mt-1"
            data-testid={'close-qr-code'}
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

export default QrCode;
